import React, { useState, useEffect } from 'react';
import { Trophy, Filter, TrendingUp, Calendar, DollarSign, RefreshCw } from 'lucide-react';
import axios from 'axios';
import API_URL from '../config';

// 1. Mapeo inteligente y dinámico de deportes por palabras clave
const PALABRAS_CLAVE_DEPORTES = [
  { keywords: ['⚽', 'soccer', 'futbol', 'football', 'league', 'liga', 'mls', 'serie a', 'champions', 'premier'], icon: '⚽' },
  { keywords: ['🏀', 'nba', 'wnba', 'baloncesto', 'basketball'], icon: '🏀' },
  { keywords: ['🏒', 'nhl', 'hockey'], icon: '🏒' },
  { keywords: ['⚾', 'mlb', 'beisbol', 'baseball'], icon: '⚾' },
  { keywords: ['🏈', 'nfl', 'ncaaf', 'futbol americano'], icon: '🏈' },
  { keywords: ['🎾', 'tennis', 'tenis', 'atp', 'wta', 'wimbledon'], icon: '🎾' },
  { keywords: ['🤾', 'handball', 'balonmano'], icon: '🤾' },
  { keywords: ['🏏', 'criquet', 'cricket'], icon: '🏏' },
  { keywords: ['🥊', 'boxeo', 'boxing', 'ufc', 'mma'], icon: '🥊' }
];

const ICONO_GENERAL = '🎯'; // Icono por defecto para cualquier otro deporte nuevo

// Función para procesar y retornar el icono correcto
const obtenerIconoDeporte = (nombreDeporte) => {
  if (!nombreDeporte) return ICONO_GENERAL;
  
  const nombreMinuscula = nombreDeporte.toLowerCase();
  
  const coincidencia = PALABRAS_CLAVE_DEPORTES.find(grupo => 
    grupo.keywords.some(keyword => nombreMinuscula.includes(keyword))
  );

  return coincidencia ? coincidencia.icon : ICONO_GENERAL;
};

export default function SportsBetsView() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('Todos');
  const [availableSports, setAvailableSports] = useState([]);

  const fetchSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/api/daily-summary`);
      setSummary(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableSports = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/available-sports`);
      setAvailableSports(response.data);
    } catch (err) {
      console.error('Error al cargar deportes disponibles:', err);
      // Si falla, usar los filtros derivados de los picks como fallback
      if (summary?.top_picks) {
        const deportesExistentes = summary.top_picks.map(pick => pick.deporte);
        setAvailableSports([...new Set(deportesExistentes.filter(Boolean))]);
      }
    }
  };

  useEffect(() => {
    fetchSummary();
    fetchAvailableSports();
  }, []);

  // Filtros dinámicos basados en la colección daily_value_bets (todos los deportes disponibles)
  const sportFilters = React.useMemo(() => {
    if (availableSports.length > 0) {
      return ['Todos', ...availableSports];
    }
    // Fallback: derivar de los picks si no hay deportes disponibles
    if (!summary?.top_picks) return ['Todos'];
    const deportesExistentes = summary.top_picks.map(pick => pick.deporte);
    return ['Todos', ...new Set(deportesExistentes.filter(Boolean))];
  }, [availableSports, summary]);

  // 1. Filtrado inicial por deporte seleccionado
  const filteredPicks = summary?.top_picks 
    ? (filter === 'Todos' 
        ? summary.top_picks 
        : summary.top_picks.filter(pick => pick.deporte === filter))
    : [];

  // 2. Ordenado cronológico (Menor a Mayor fecha_partido)
  const sortedFilteredPicks = React.useMemo(() => {
    return [...filteredPicks].sort((a, b) => {
      if (!a.fecha_partido) return 1;
      if (!b.fecha_partido) return -1;
      return new Date(a.fecha_partido) - new Date(b.fecha_partido);
    });
  }, [filteredPicks]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const formatGameDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEvColor = (ev) => {
    if (ev >= 25) return 'text-red-500 bg-red-500/10';
    if (ev >= 15) return 'text-orange-500 bg-orange-500/10';
    if (ev >= 10) return 'text-yellow-500 bg-yellow-500/10';
    return 'text-green-500 bg-green-500/10';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center animate-in fade-in">
        <div className="bg-slate-900 border border-red-500/30 p-8 rounded-2xl max-w-md">
          <Trophy size={48} className="text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-black text-white mb-2">Sin datos disponibles</h2>
          <p className="text-slate-400 mb-4">{error}</p>
          <button 
            onClick={fetchSummary}
            className="px-6 py-2 bg-yellow-500 text-slate-900 font-bold rounded-lg hover:bg-yellow-400 transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-black italic uppercase text-white flex items-center gap-3">
            <Trophy className="text-yellow-500" size={32} />
            Análisis Logros Deportes
          </h1>
          <p className="text-slate-400 mt-1">Oportunidades de valor detectadas hoy</p>
        </div>
        
        <button 
          onClick={fetchSummary}
          className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors"
        >
          <RefreshCw size={16} />
          Actualizar
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-slate-900/50 border border-slate-800 p-5 rounded-xl">
          <div className="flex items-center gap-3 mb-2">
            <Calendar size={18} className="text-yellow-500" />
            <span className="text-slate-400 text-sm font-medium">Fecha</span>
          </div>
          <p className="text-white font-black">{summary?.fecha_resumen ? formatDate(summary.fecha_resumen) : 'N/A'}</p>
        </div>

        <div className="bg-slate-900/50 border border-slate-800 p-5 rounded-xl">
          <div className="flex items-center gap-3 mb-2">
            <Trophy size={18} className="text-yellow-500" />
            <span className="text-slate-400 text-sm font-medium">Oportunidades</span>
          </div>
          <p className="text-white font-black text-2xl">{summary?.total_oportunidades_detectadas || 0}</p>
        </div>

        <div className="bg-slate-900/50 border border-slate-800 p-5 rounded-xl">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp size={18} className="text-yellow-500" />
            <span className="text-slate-400 text-sm font-medium">Mejor EV</span>
          </div>
          <p className="text-green-500 font-black text-2xl">
            {summary?.top_picks?.[0] ? `${summary.top_picks[0].ev_porcentaje}%` : 'N/A'}
          </p>
        </div>

        <div className="bg-slate-900/50 border border-slate-800 p-5 rounded-xl">
          <div className="flex items-center gap-3 mb-2">
            <DollarSign size={18} className="text-yellow-500" />
            <span className="text-slate-400 text-sm font-medium">Mejor Cuota</span>
          </div>
          <p className="text-white font-black text-2xl">
            {summary?.top_picks?.[0] ? `@ ${summary.top_picks[0].cuota}` : 'N/A'}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <Filter size={18} className="text-slate-400" />
        <div className="flex flex-wrap gap-2">
          {sportFilters.map(sport => (
            <button
              key={sport}
              onClick={() => setFilter(sport)}
              className={`px-4 py-2 rounded-lg font-bold text-xs uppercase tracking-wider transition-all ${
                filter === sport 
                  ? 'bg-yellow-500 text-slate-900' 
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              {sport}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-slate-900/50 border border-slate-800 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-950/50">
                <th className="text-left p-4 text-slate-400 font-bold text-xs uppercase tracking-wider">Evento</th>
                <th className="text-left p-4 text-slate-400 font-bold text-xs uppercase tracking-wider">Deporte</th>
                <th className="text-left p-4 text-slate-400 font-bold text-xs uppercase tracking-wider">Fecha Juego</th>
                <th className="text-left p-4 text-slate-400 font-bold text-xs uppercase tracking-wider">Selección</th>
                <th className="text-center p-4 text-slate-400 font-bold text-xs uppercase tracking-wider">Punto</th>
                <th className="text-center p-4 text-slate-400 font-bold text-xs uppercase tracking-wider">Cuota</th>
                <th className="text-center p-4 text-slate-400 font-bold text-xs uppercase tracking-wider">EV%</th>
                <th className="text-left p-4 text-slate-400 font-bold text-xs uppercase tracking-wider">Casa</th>
              </tr>
            </thead>
            <tbody>
              {sortedFilteredPicks.map((pick, index) => (
                <tr key={index} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{obtenerIconoDeporte(pick.deporte)}</span>
                      <span className="text-white font-medium text-sm">{pick.evento}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-slate-300 text-sm">{pick.deporte}</span>
                  </td>
                  <td className="p-4">
                    <span className="text-slate-400 text-xs font-semibold whitespace-nowrap">
                      {formatGameDate(pick.fecha_partido)}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className="text-yellow-500 font-bold text-sm">{pick.seleccion}</span>
                  </td>
                  <td className="p-4 text-center">
                    <span className="text-slate-400 text-sm">{pick.punto || '-'}</span>
                  </td>
                  <td className="p-4 text-center">
                    <span className="text-green-400 font-black text-lg">{pick.cuota}</span>
                  </td>
                  <td className="p-4 text-center">
                    <span className={`inline-flex items-center px-2 py-1 rounded font-black text-sm ${getEvColor(pick.ev_porcentaje)}`}>
                      {pick.ev_porcentaje}%
                    </span>
                  </td>
                  <td className="p-4">
                    <span className="text-slate-300 text-sm">{pick.casa}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {sortedFilteredPicks.length === 0 && (
          <div className="p-12 text-center text-slate-500">
            No hay picks para el filtro seleccionado
          </div>
        )}
      </div>

      <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-xl">
        <p className="text-slate-400 text-sm text-center">
          <span className="text-yellow-500 font-bold">Nota:</span> Las cuotas mostradas son de casas de apuestas. 
          Bet Responsibly.
        </p>
      </div>
    </div>
  );
}